import React, { useState, useMemo } from 'react';
import { useAdmin } from '../../contexts/AdminContext';

const AdminInquiries: React.FC = () => {
  const { inquiries, replyToInquiry, updateInquiry, adminUser } = useAdmin();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'status' | 'category'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedInquiry, setSelectedInquiry] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyModalOpen, setReplyModalOpen] = useState(false);

  // Category List
  const categories = ['product', 'delivery', 'payment', 'return', 'general'];
  const categoryLabels = {
    product: 'Product Inquiry',
    delivery: 'Shipping Inquiry',
    payment: 'Order/Payment Inquiry',
    return: 'Cancel/Refund Inquiry',
    general: 'General Inquiry'
  };

  // Filtered and sorted Inquiry
  const filteredAndSortedInquiries = useMemo(() => {
    let filtered = inquiries.filter(inquiry => {
      const matchesSearch = inquiry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           inquiry.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           inquiry.userEmail.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !filterStatus || inquiry.status === filterStatus;
      const matchesCategory = !filterCategory || inquiry.category === filterCategory;
      return matchesSearch && matchesStatus && matchesCategory;
    });

    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'category':
          aValue = a.category;
          bValue = b.category;
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [inquiries, searchTerm, filterStatus, filterCategory, sortBy, sortOrder]);

  const handleReply = (inquiryId: string) => {
    setSelectedInquiry(inquiryId);
    const inquiry = inquiries.find(i => i.id === inquiryId);
    setReplyText(inquiry?.adminReply || '');
    setReplyModalOpen(true);
  };

  const handleSubmitReply = () => {
    if (!selectedInquiry || !replyText.trim()) return;

    replyToInquiry(selectedInquiry, replyText.trim(), adminUser?.name || 'Admin');
    setReplyModalOpen(false);
    setSelectedInquiry(null);
    setReplyText('');
  };

  const handleStatusChange = (inquiryId: string, status: 'pending' | 'answered' | 'closed') => {
    updateInquiry(inquiryId, { status });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' };
      case 'answered':
        return { label: 'Replied', color: 'bg-green-100 text-green-800' };
      case 'closed':
        return { label: 'Closed', color: 'bg-neutral-100 text-neutral-800' };
      default:
        return { label: status, color: 'bg-neutral-100 text-neutral-800' };
    }
  };

  const stats = {
    total: inquiries.length,
    pending: inquiries.filter(i => i.status === 'pending').length,
    answered: inquiries.filter(i => i.status === 'answered').length,
    closed: inquiries.filter(i => i.status === 'closed').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">1:1 Inquiry Management</h1>
          <p className="mt-2 text-sm text-neutral-700">
            {inquiries.length} inquiry(s) registered in total.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-forest-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">All Inquiries</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.total}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">Awaiting Reply</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.pending}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">Replied</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.answered}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">Closed</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.closed}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-neutral-700 mb-1">
                Search
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-md leading-5 bg-white placeholder-neutral-500 focus:outline-none focus:placeholder-neutral-400 focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                  placeholder="Title, Content, Email Search..."
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label htmlFor="status-filter" className="block text-sm font-medium text-neutral-700 mb-1">
                Status
              </label>
              <select
                id="status-filter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
              >
                <option value="">All Status</option>
                <option value="pending">Awaiting Reply</option>
                <option value="answered">Replied</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <label htmlFor="category-filter" className="block text-sm font-medium text-neutral-700 mb-1">
                Category
              </label>
              <select
                id="category-filter"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {categoryLabels[category as keyof typeof categoryLabels]}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div>
              <label htmlFor="sort" className="block text-sm font-medium text-neutral-700 mb-1">
                Sort
              </label>
              <div className="flex space-x-2">
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="flex-1 px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                >
                  <option value="createdAt">Date Created</option>
                  <option value="status">Status</option>
                  <option value="category">Category</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border border-neutral-300 rounded-md shadow-sm bg-white hover:bg-neutral-50 focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inquiries List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {filteredAndSortedInquiries.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-neutral-900">No inquiries found</h3>
            <p className="mt-1 text-sm text-neutral-500">Waiting for new inquiries.</p>
          </div>
        ) : (
          <ul className="divide-y divide-neutral-200">
            {filteredAndSortedInquiries.map((inquiry) => {
              const statusBadge = getStatusBadge(inquiry.status);
              return (
                <li key={inquiry.id} className="hover:bg-neutral-50">
                  <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center space-x-3">
                            <h3 className="text-sm font-medium text-neutral-900 truncate">
                              {inquiry.title}
                            </h3>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
                              {statusBadge.label}
                            </span>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-forest-100 text-forest-800">
                              {categoryLabels[inquiry.category as keyof typeof categoryLabels]}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center text-sm text-neutral-500">
                            <span className="truncate">
                              {inquiry.userEmail}
                            </span>
                            <span className="mx-2">•</span>
                            <span>
                              {formatDate(inquiry.createdAt)}
                            </span>
                            {inquiry.orderNumber && (
                              <>
                                <span className="mx-2">•</span>
                                <span>Order #: {inquiry.orderNumber}</span>
                              </>
                            )}
                          </div>
                          <div className="mt-2 text-sm text-neutral-600">
                            {inquiry.content.length > 100 
                              ? `${inquiry.content.substring(0, 100)}...` 
                              : inquiry.content
                            }
                          </div>
                          {inquiry.adminReply && (
                            <div className="mt-3 p-3 bg-forest-50 rounded-lg">
                              <div className="text-xs text-forest-400 font-medium mb-1">
                                Admin Reply ({inquiry.answeredBy} • {formatDate(inquiry.answeredAt!)})
                              </div>
                              <div className="text-sm text-forest-900">
                                {inquiry.adminReply}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="ml-6 flex items-center space-x-2">
                        <select
                          value={inquiry.status}
                          onChange={(e) => handleStatusChange(inquiry.id, e.target.value as any)}
                          className="text-sm border border-neutral-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                        >
                          <option value="pending">Awaiting Reply</option>
                          <option value="answered">Replied</option>
                          <option value="closed">Closed</option>
                        </select>
                        <button
                          onClick={() => handleReply(inquiry.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-forest-700 bg-forest-100 hover:bg-forest-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500"
                        >
                          {inquiry.adminReply ? 'Edit Reply' : 'Write Reply'}
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Reply Modal */}
      {replyModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-neutral-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-forest-100">
                  <svg className="h-6 w-6 text-forest-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-neutral-900">
                    Inquiry Reply
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-neutral-500">
                      Please write a reply to the customer's inquiry.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={6}
                  className="block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                  placeholder="Enter reply content..."
                />
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={handleSubmitReply}
                  disabled={!replyText.trim()}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-forest-400 text-base font-medium text-white hover:bg-forest-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Reply
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReplyModalOpen(false);
                    setSelectedInquiry(null);
                    setReplyText('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-neutral-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInquiries;